import React, { useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';

const AREAS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

function chunks(values, size) {
  const result = [];
  for (let index = 0; index < values.length; index += size) {
    result.push(values.slice(index, index + size));
  }
  return result;
}

function locationUrl(code) {
  return `${window.location.origin}/platz/${encodeURIComponent(code)}`;
}

function describeLocation(code) {
  const match = code.match(/^DB-([A-Z])-(\d{2})$/);
  if (!match) return '';
  return `Bereich ${match[1]} · Stellfeld ${Number(match[2])}`;
}

export default function LocationPrint({ onClose }) {
  const [bereich, setBereich] = useState('A');
  const [ersterPlatz, setErsterPlatz] = useState(1);
  const [letzterPlatz, setLetzterPlatz] = useState(8);
  const [locations, setLocations] = useState([]);
  const [error, setError] = useState('');

  const pages = useMemo(() => chunks(locations, 8), [locations]);

  function handleGenerate(event) {
    event.preventDefault();
    const first = Number.parseInt(ersterPlatz, 10);
    const last = Number.parseInt(letzterPlatz, 10);
    if (!Number.isInteger(first) || !Number.isInteger(last) || first < 1 || last > 99 || last < first) {
      setError('Bitte einen gültigen Platzbereich zwischen 1 und 99 angeben.');
      return;
    }

    const generated = [];
    for (let place = first; place <= last; place += 1) {
      generated.push(`DB-${bereich}-${String(place).padStart(2, '0')}`);
    }
    if (generated.length > 100) {
      setError('Bitte höchstens 100 Platzschilder auf einmal erzeugen.');
      return;
    }
    setError('');
    setLocations(generated);
  }

  return (
    <div className="label-print-overlay" style={styles.overlay}>
      <div className="label-print-modal" style={styles.modal}>
        <div style={styles.header}>
          <div>
            <div style={styles.kicker}>Dachboden organisieren</div>
            <h2 style={styles.title}>Platzschilder drucken</h2>
            <div style={styles.subtitle}>Ein Schild je dauerhaftem Lagerplatz · Avery L7914 · 8 pro A4-Bogen</div>
          </div>
          <button type="button" style={styles.close} onClick={onClose} aria-label="Schließen">×</button>
        </div>

        <div style={styles.guide}>
          <strong>So markierst du die freie Fläche:</strong> Mit farbigem Bodenband Bereiche und Stellfelder abgrenzen.
          Das Schild kommt an den vorderen Rand des Feldes, außerhalb des Laufwegs. Ebene 1 ist später die unterste Box im Stapel.
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <form style={styles.form} onSubmit={handleGenerate}>
          <label style={styles.label}>
            Bereich
            <select style={styles.input} value={bereich} onChange={event => setBereich(event.target.value)}>
              {AREAS.map(value => <option value={value} key={value}>{value}</option>)}
            </select>
          </label>
          <label style={styles.label}>
            Erster Platz
            <input style={styles.input} type="number" min="1" max="99" value={ersterPlatz}
              onChange={event => setErsterPlatz(event.target.value)} />
          </label>
          <label style={styles.label}>
            Letzter Platz
            <input style={styles.input} type="number" min="1" max="99" value={letzterPlatz}
              onChange={event => setLetzterPlatz(event.target.value)} />
          </label>
          <button type="submit" style={styles.primaryButton}>Schilder erzeugen</button>
        </form>

        {locations.length > 0 && (
          <>
            <div style={styles.selectionHeader}>
              <div>
                <strong>{locations.length} Platzschilder</strong>
                <div style={styles.small}>{pages.length} A4-Bogen{pages.length !== 1 ? 'en' : ''}</div>
              </div>
              <button type="button" style={styles.printButton} onClick={() => window.print()}>Drucken</button>
            </div>
            <div style={styles.previewWrap}>
              <QRCodeSVG value={locationUrl(locations[0])} size={92} level="M" marginSize={1} />
              <div>
                <div style={styles.previewCode}>{locations[0]}</div>
                <div style={styles.small}>{describeLocation(locations[0])}</div>
              </div>
            </div>
            <div style={styles.printHint}>
              Im Druckdialog: A4, Skalierung 100 %, Ränder „Keine“ und Kopf-/Fußzeilen aus.
            </div>
          </>
        )}
      </div>

      <div className="label-print-pages">
        {pages.map((page, pageIndex) => (
          <div className="label-print-sheet" key={`location-page-${pageIndex}`}>
            {page.map(code => (
              <div className="label-print-label location-print-label" key={code}>
                <QRCodeSVG className="label-print-qr" value={locationUrl(code)} level="M" marginSize={1} />
                <div className="location-print-copy">
                  <div className="location-print-code">{code}</div>
                  <div className="location-print-description">{describeLocation(code)}</div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(23,32,51,0.62)', backdropFilter: 'blur(8px)', padding: 16, overflowY: 'auto' },
  modal: { width: '100%', maxWidth: 760, margin: '16px auto 32px', border: '1px solid rgba(221,226,235,0.9)', borderRadius: 20, padding: 26, background: '#fff', boxShadow: '0 24px 70px rgba(25,35,62,0.22)' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 18 },
  kicker: { color: '#137a62', fontSize: 12, fontWeight: 850, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 5 },
  title: { margin: 0, color: '#213A44', fontSize: 25, letterSpacing: '-0.035em' },
  subtitle: { color: '#697386', fontSize: 13, marginTop: 5 },
  close: { border: 'none', borderRadius: 12, width: 40, height: 40, background: '#f0f2f6', color: '#657083', fontSize: 25, lineHeight: 1 },
  guide: { padding: 14, borderRadius: 13, background: '#eef8f5', border: '1px solid #cce9e0', color: '#285d50', fontSize: 13, lineHeight: 1.55, marginBottom: 16 },
  form: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(115px, 1fr))', alignItems: 'end', gap: 10 },
  label: { display: 'flex', flexDirection: 'column', gap: 5, color: '#374151', fontSize: 13, fontWeight: 750 },
  input: { width: '100%', minWidth: 0, padding: '11px 12px', border: '1px solid #d9dee8', borderRadius: 11, fontSize: 15, background: '#fff' },
  primaryButton: { border: 'none', borderRadius: 11, minHeight: 43, padding: '11px 15px', background: '#0B6766', color: '#fff', fontSize: 14, fontWeight: 800 },
  error: { padding: 10, borderRadius: 8, marginBottom: 12, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', fontSize: 14 },
  selectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 20, paddingTop: 16, borderTop: '1px solid #e2e8f0' },
  printButton: { border: 'none', borderRadius: 11, padding: '12px 23px', background: '#137a62', color: '#fff', fontSize: 14, fontWeight: 800 },
  previewWrap: { marginTop: 14, minHeight: 145, border: '1px solid #d9dee8', borderRadius: 13, padding: 16, display: 'flex', alignItems: 'center', gap: 18, background: '#fff' },
  previewCode: { fontSize: 26, fontWeight: 900, color: '#213A44', letterSpacing: '0.03em' },
  small: { fontSize: 12, color: '#64748b', marginTop: 3, fontWeight: 400 },
  printHint: { marginTop: 12, color: '#475569', fontSize: 13, lineHeight: 1.5 },
};
